import * as React from "react";

import { PublicInvoice } from "api/billingPublic";
import { Badge, type StatusTone } from "Components/ui";
import { cn } from "utils/cn";
import { formatDate, formatINR, todayIST } from "utils/format";

// Client-facing wording ("Unpaid", "Cancelled"), so not the admin StatusBadge labels.
const STATUS: Record<PublicInvoice["status"], { label: string; tone: StatusTone }> = {
  draft: { label: "Draft", tone: "neutral" },
  issued: { label: "Unpaid", tone: "info" },
  partially_paid: { label: "Partly paid", tone: "warning" },
  paid: { label: "Paid", tone: "success" },
  void: { label: "Cancelled", tone: "neutral" },
};

interface InvoiceSummaryProps {
  invoice: PublicInvoice;
  compact?: boolean;
}

/** Client-facing invoice card: number, client, items and totals. */
const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({ invoice, compact = false }) => {
  const status = STATUS[invoice.status] || STATUS.issued;
  const overdue =
    (invoice.status === "issued" || invoice.status === "partially_paid") &&
    invoice.amountDue > 0 &&
    invoice.dueDate < todayIST();

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-subtle-foreground">
            Invoice
          </p>
          <p className="mt-0.5 break-words font-mono text-lg font-semibold text-foreground">
            {invoice.invoiceNumber || "—"}
          </p>
          {invoice.tenantName && (
            <p className="text-sm text-muted-foreground">{invoice.tenantName}</p>
          )}
          {invoice.title && <p className="text-sm text-muted-foreground">{invoice.title}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge tone={status.tone} size="md" dot>
            {status.label}
          </Badge>
          {overdue && (
            <Badge tone="danger" size="md" dot>
              Overdue
            </Badge>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Due on <span className="font-semibold text-foreground">{formatDate(invoice.dueDate)}</span>
      </p>

      {!compact && invoice.items.length > 0 && (
        <ul className="mt-5 divide-y divide-border border-y border-border">
          {invoice.items.map((item, index) => (
            <li
              key={`${item.description}-${index}`}
              className="flex items-start justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="break-words text-sm text-foreground">{item.description}</p>
                {item.qty !== 1 && (
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {item.qty} × {formatINR(item.unitPrice)}
                  </p>
                )}
              </div>
              <p
                className={cn(
                  "whitespace-nowrap text-sm tabular-nums",
                  item.amount < 0 ? "text-success-text" : "text-foreground"
                )}
              >
                {formatINR(item.amount)}
              </p>
            </li>
          ))}
        </ul>
      )}

      <dl className="mt-4 space-y-1.5 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Total</dt>
          <dd className="tabular-nums text-foreground">{formatINR(invoice.total)}</dd>
        </div>
        {invoice.amountPaid > 0 && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Paid</dt>
            <dd className="tabular-nums text-foreground">{formatINR(invoice.amountPaid)}</dd>
          </div>
        )}
        <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2">
          <dt className="font-semibold text-foreground">Amount due</dt>
          <dd className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
            {formatINR(invoice.amountDue)}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default InvoiceSummary;
